import FilterControlFactory from "../filterControlFactory";
import AndFilterControl from "./AndFilterControl";
import OrFilterControl from "./OrFilterControl";

import type AbstractFilterControl from "./AbstractFilterControl";
import type AbstractSpatialFilterControl from "./AbstractSpatialFilterControl";
import type { Map } from "@esWebAPI/es/map";
import type { FeatureViewModel } from "@esWebAPI/es/map/FeatureViewModel";
import type LayerViewModel from "@esWebAPI/es/map/LayerViewModel";
import type { Criteria } from "@esWebAPI/es/model/Criteria";
import type { Attribute, Feature } from "@esWebAPI/es/model/Feature";
import type { ILayerView, LayerInfo } from "@esWebAPI/es/model/Layer";
import type { IEventChannel } from "@esWebAPI/es/model/Models";
import type FormatterFuncs from "@esWebAPI/es/util/formatterFuncs";

export type FilterObject = {
    attributes: Attribute[],
    filterRanges: any[],
    layerViewModel: LayerViewModel,
    featureViewModel: FeatureViewModel,
    FeatureSourceCriteria?: Criteria,
    analysis?: any,
    isTerritoryMap: boolean,
    canLockFn?: (filter: AbstractFilterControl) => boolean,
    checkBeforeLockFn?: (layerViewId: number) => JQueryDeferred<boolean>
};

export default class FilterControl {
    private filterObject: FilterObject;
    private layerViewModel: LayerViewModel;
    private layerInfo: LayerInfo;
    private map: Map;
    private formatterFuncs: FormatterFuncs;

    private filters: AbstractFilterControl[];
    private fixedDynamicAttributes: string[];
    private eventChannel: IEventChannel;
    // True while we are pushing panel-edits to the model, so the resulting
    // onSetCriteria event does not trigger a panel rebuild and clobber the user's edits.
    private suppressOnSetCriteria: boolean = false;
    // True when this FilterControl subscribed to the layer view's onSetCriteria event
    // (only on territory pages - standalone or SFDC-embedded - where the LLM tool can
    // apply criteria programmatically).
    private listeningForCriteriaChanges: boolean = false;

    constructor(filterObject: FilterObject, map: Map, formatterFuncs: FormatterFuncs) {
        this.filterObject = filterObject;
        this.layerViewModel = filterObject.layerViewModel;
        this.layerInfo = this.layerViewModel.getLayerInfo();
        this.map = map;
        this.formatterFuncs = formatterFuncs;

        this.filters = [];

        this.fixedDynamicAttributes = [];
        if (es.isPublicAccess) {
            this.layerViewModel.getDynamicAttributes().forEach((da) => {
                if (!es.util.criteria.find(this.layerViewModel.getCriteriaObjRef(), {'name': da.criteriaName})) {
                    this.fixedDynamicAttributes.push(da.name);
                }
            });
        }

        this.eventChannel = es.util.createEventChannel({
            onFilterAdded: 1,
            onFilterRemoved: 1,
            onFilterRefreshed: 1,
            onDestroy: 1,
            onUpdateModelFromControls: 1
        }, "es.control.FilterControl");

        this.init = this.init.bind(this);
        this.destroy = this.destroy.bind(this);
        this.getFilters = this.getFilters.bind(this);
        this.getFiltersByAttributeName = this.getFiltersByAttributeName.bind(this);
        this.getSpatialFilter = this.getSpatialFilter.bind(this);
        this.getLayerViewId = this.getLayerViewId.bind(this);
        this.getAttributes = this.getAttributes.bind(this);
        this.getLockedCriteria = this.getLockedCriteria.bind(this);
        this.getEventChannel = this.getEventChannel.bind(this);
        this.canLockFilter = this.canLockFilter.bind(this);
        this.canUnlockFilter = this.canUnlockFilter.bind(this);
        this.checkBeforeLock = this.checkBeforeLock.bind(this);
        this.attributeLockFilter = this.attributeLockFilter.bind(this);
        this.hasAggregateFilter = this.hasAggregateFilter.bind(this);
        this.attributeHasControl = this.attributeHasControl.bind(this);
        this.refresh = this.refresh.bind(this);
        this.createFromCriteria = this.createFromCriteria.bind(this);
        this.createAttributeFilter = this.createAttributeFilter.bind(this);
        this.createSpatialFilter = this.createSpatialFilter.bind(this);
        this.createOr = this.createOr.bind(this);
        this.createAnd = this.createAnd.bind(this);
        this.removeFilterControlsForAttribute = this.removeFilterControlsForAttribute.bind(this);
        this.removeFilter = this.removeFilter.bind(this);
        this.removeAllFilters = this.removeAllFilters.bind(this);
        this.moveFilter = this.moveFilter.bind(this);
        this.setAttributesInfo = this.setAttributesInfo.bind(this);
        // private TS methods, so we can use bind to facilitate usage in callbacks (otherwise they can get TypeError: Cannot read private member)
        this.onEditFeatures = this.onEditFeatures.bind(this);
        this.onBatchEditFeatures = this.onBatchEditFeatures.bind(this);
        this.onUpdateLayerView = this.onUpdateLayerView.bind(this);
        this._init = this._init.bind(this);
        this.onFilterCreated = this.onFilterCreated.bind(this);
        this.updateLayerViewModel = this.updateLayerViewModel.bind(this);
        this.onRemovedFilter = this.onRemovedFilter.bind(this);
        this._refresh = this._refresh.bind(this);
        this.onLayerViewSetCriteria = this.onLayerViewSetCriteria.bind(this);
    }

    /**
     * Initialise the filterControl with both the layerViewModel criteria and the featureSource criteria
     *
     * @returns A promise which is resolved when every filter are created
     */
    init() {
        es.api.getEventChannel()
            .addListener("onEditFeatures", this.onEditFeatures)
            .addListener("onBatchEditFeatures", this.onBatchEditFeatures)
            .addListener("onSyncDatasetFinish", this._init)
            .addListener('onUpdateLayerView', this.onUpdateLayerView);

        // Territory pages host the AI assistant chat that can apply a criteria
        // programmatically via setCriteria - so there we need to rebuild the panel rows
        // when the model criteria changes outside of the panel itself. This includes the
        // SFDC-embedded territory page: TerritoryAssist runs inside the Salesforce canvas
        // too (an earlier !es.isEmbeddedInSFDC exclusion here left the panel empty there).
        if (window["esApp"] && esApp.isTerritoryPage?.()) { // TOR9-22121 - esMobile won't even have the esApp global var defined, so, we have to check the window context for it
            this.layerViewModel.getEventChannel().addListener("onSetCriteria", this.onLayerViewSetCriteria);
            this.listeningForCriteriaChanges = true;
        }

        return this._init().fail(es.error.handleJSError);
    }

    destroy(isUIDestroy?: boolean) {
        this.clearFilters();
        this.eventChannel.fireEvent("onFilterRemoved", !!isUIDestroy);
        this.eventChannel.fireEvent("onDestroy");

        es.api.getEventChannel()
            .removeListener("onEditFeatures", this.onEditFeatures)
            .removeListener("onBatchEditFeatures", this.onBatchEditFeatures)
            .removeListener("onSyncDatasetFinish", this._init)
            .removeListener("onUpdateLayerView", this.onUpdateLayerView);

        if (this.listeningForCriteriaChanges) {
            this.layerViewModel.getEventChannel().removeListener("onSetCriteria", this.onLayerViewSetCriteria);
            this.listeningForCriteriaChanges = false;
        }
        //this.filterObject = this.map = this.eventChannel = this.layerViewModel = this.layerInfo = null;
    }

    // Rebuild panel rows from the model's current criteria. Called when something other
    // than the panel itself (e.g. the AI assistant tool propose_layer_view_filter) changes
    // the layer view's criteria. The suppress flag keeps user-driven panel edits from
    // triggering a self-rebuild via the same event.
    private onLayerViewSetCriteria() {
        if (this.suppressOnSetCriteria) {
            return;
        }
        this._init().fail(es.error.handleJSError);
    }

    /**
     * 
     */
    getFilters() {
        return this.filters.slice();
    }

    /**
     * 
     * @param attrName 
     * @returns 
     */
    getFiltersByAttributeName(attrName: string) {
        return this.filters.flatMap(getLeafFilters).filter(f => f.getAttrName() === attrName);
    }


    /**
     * Return the existing spatial filter if any
     *
     * @returns 
     */
    getSpatialFilter(): AbstractSpatialFilterControl | undefined {
        function recursiveGetSpatialFilter(filters: AbstractFilterControl[]) {
            for (let i=0; i<filters.length; i++) {
                const f = filters[i];
                
                if (f.isSpatial()) {
                    return f as AbstractSpatialFilterControl;
                }
                
                if (f.getType() === "And" || f.getType() === "Or") {
                    return recursiveGetSpatialFilter(f.getFilters());
                }
            }
            
            return undefined;
        }
        
        return recursiveGetSpatialFilter(this.filters);
    }

    /**
     * @returns the layerView id this filterControl is filtering on.
     */
    getLayerViewId() {
        return this.layerViewModel.getId();
    }

    /**
     * 
     * @returns 
     */
    getAttributes() {
        return this.filterObject.attributes;
    }

    /**
     * 
     * @returns 
     */
    getLockedCriteria( ){
        return this.filterObject.FeatureSourceCriteria;
    }

    /**
     * 
     * @returns 
     */
    getEventChannel() {
        return this.eventChannel;
    }

    /**
     * 
     * @param filter 
     * 
     * @returns 
     */
    canLockFilter(filter: AbstractFilterControl) {
        if (this.getFiltersByAttributeName(filter.getAttrName()).length > 1) {
            return false;
        }

        if (this.filterObject.canLockFn) {
            return this.filterObject.canLockFn(filter);
        }

        return !es.isPublicAccess && !this.filterObject.isTerritoryMap && filter.canLock();
    }

    /**
     * 
     * @param filter 
     * 
     * @returns 
     */
    canUnlockFilter(filter: AbstractFilterControl) {
        if (!this.attributeLockFilter(filter.getAttrName())) {
            return false;
        }
        
        if (this.getFiltersByAttributeName(filter.getAttrName()).length !== 1) {
            return false;
        }
        
        // TOR9-18224 In a schedule if we can't lock, we won't allow to unlock as well
        if (this.map.meta.isSchedule()) {
            if (this.filterObject.canLockFn && !this.filterObject.canLockFn(filter)) {
                return false;
            }
        }
        
        return filter.canLock(); // should always be true, how else it would be locked?!
    }

    /**
     * 
     * @returns 
     */
    checkBeforeLock() {
        if (this.filterObject.checkBeforeLockFn) {
            return this.filterObject.checkBeforeLockFn(this.getLayerViewId());
        } else {
            return es.util.deferred.resolved();
        }
    }

    /**
     * accessor to know if an attribute has a lock filter
     * 
     * @param attrName
     * 
     * @returns 
     */
    attributeLockFilter(attrName: string): boolean {
        return this.filters.some((f) => {
            return f.getAttrName() === attrName && f.locked === true;
        });
    }

    /**
     * 
     * @returns 
     */
    hasAggregateFilter() {
        return this.filters.find((filter) => filter.getType() === "Aggregate") !== undefined;
    }

    /**
     * accessor to know if an attribute is filtered on
     * 
     * @param attrName
     * 
     * @returns 
     */
    attributeHasControl(attrName: string): boolean {
        return this.filters.flatMap(getLeafFilters).some((f) => f.getAttrName() === attrName);
    }

    /**
     * 
     */
    refresh() {
        this.eventChannel.fireEvent("onFilterRefreshed");
    }

    /**
     * Create a filter from a given criteria and trigger the event "onFilterAdded"
     *
     * @param criteria source criteria
     * @param isFeatureSource weither the filter will be locked
     * @param silent flag to define if the event should be triggered
     * @returns Promise<[Filter]>
     */
    createFromCriteria(criteria: Criteria | null | undefined, isFeatureSource: boolean, silent?: boolean): JQueryDeferred<AbstractFilterControl[]> {
        const criterias = criteria ? [criteria] : [];

        // TOR9-20515 - All doesn't have an associated filter component but we can simply ignore it
        // (All AND "something" is the same as just "something")
        // This should never happen but seems to be happening in some rare cases
        const dfds = criterias.filter(crit => crit.$type != 'All').map((crit) => {
            const attrs = es.control.filterUtil.getAttributesForCriteria(this.filterObject.attributes, crit) || []; // it is returning undefined if no attr names in criteria...

            const filterDfd = FilterControlFactory.fromCriteria(this.layerViewModel, crit, attrs, this.layerInfo.featureInfo.attributeRanges, this.map.layer, this.filterObject.analysis, this.formatterFuncs);
            if (!filterDfd) {
                throw new Error("No Filter available for criteria", crit);
            }

            return filterDfd.then((filter) => {
                filter.setLock(isFeatureSource);
                return filter;
            });
        });

        return es.util.deferred.all(dfds).then((filterList) => {
            filterList.forEach((filter) => this.onFilterCreated(filter, null, true));

            if (!silent && this.filters.length) {
                this.eventChannel.fireEvent("onFilterAdded");
            }

            return filterList;
        }).fail(es.error.handleJSError);
    }

    /**
     * Create a new filter on an attribute. Will update the layerViewModel
     *
     * @param attributeName name of attribute to filter on
     * @returns Promise a promise which will be resolved when the filter is effective
     */
    createAttributeFilter(attributeName: string, parent) {
        const usedAttributes = this.getUsedAttributes().concat(attributeName);
        return this.fetchLayerInfo(usedAttributes).then(() => {
            const range = this.layerInfo.featureInfo.attributeRanges[attributeName];
            const attribute = this.filterObject.attributes.find((a) => a.name === attributeName);
            const filterClass = FilterControlFactory.forAttribute(attribute.attrType, range);
            return new filterClass(this.layerViewModel, attribute, range, this.filterObject.analysis).init();
        }).then((f) => this.onFilterCreated(f, parent)).then((filter) => {
            return this.updateLayerViewModel().then(() => filter);
        }).fail(es.error.handleJSError);
    }
    
    /**
     * Create a new spatial filter and remove existing spatial filter if any
     * 
     * @param type spatial filter type
     * @param geom The geometry of the filter (will create geoShape if it's not a point)
     * @param feature [optional] The feature
     */
    createSpatialFilter(type: string, geom: any[], feature?: Feature): JQueryDeferred<AbstractSpatialFilterControl> {
        const removeSpatialFilter = (filters) => {
            return filters.filter(f => {  // remove existing spatialFilter
                if (['Or', 'And'].includes(f.getType())) {
                    f.getFilters().filter(f => f.isSpatial()).forEach(sf => f.removeFilter(sf, true)); // remove filters using parent if in current "level"

                    let innerFilters = f.getFilters();
                    if (innerFilters.length == 0) {
                        return false;
                    }

                    removeSpatialFilter(innerFilters); // remove recursively in different "level"
                    // removing will trigger onDestroy event that will remove it from parent and destroy parent if empty
                    innerFilters = f.getFilters(); // re-fetch to have latest

                    return innerFilters.length > 0;
                }

                if (f.isSpatial()) {
                    f.destroy(true);
                    return false;
                }

                return true;
            });
        };
        this.filters = removeSpatialFilter(this.filters);

        var dfd;
        var filterClass = FilterControlFactory.forCriteria({$type: type});
        if (geom[0] !== "PT") {
            dfd = es.api.geoShape.store({
                geom: geom
            }).then((geoShape) => {
                return new filterClass(this.layerViewModel, undefined, geoShape.key, feature, this.map.layer).init();
            });
        } else {
            dfd = new filterClass(this.layerViewModel, geom, undefined, feature, this.map.layer).init();
        }

        return dfd.then(this.onFilterCreated).then((filter) => {
            return this.updateLayerViewModel().then(() => filter);
        }).fail(es.error.handleJSError);
    }

    /**
     * Create an OrFilter
     * @param children
     * @param parent
     */
    createOr(children?: AbstractFilterControl[], parent) {
        const or = new OrFilterControl(this.layerViewModel, children);
        return this.onFilterCreated(or, parent, true);
    }

    /**
     * Create an AndFilter
     * @param children
     * @param parent
     */
    createAnd(children?: AbstractFilterControl[], parent) {
        const and = new AndFilterControl(this.layerViewModel, children);
        return this.onFilterCreated(and, parent, true);
    }

    /**
     * Remove a filter
     * 
     * @param attributeName the attribute name the filter is effective on.
     */
    removeFilterControlsForAttribute(attributeName: string) {
        this.removeAttributeFilter(attributeName);
    }

    /**
     * Remove a filter
     * 
     * @param filter the existing filter
     * 
     * @returns null if the filter has been removed or an array of string errors
     */
    removeFilter(filter: AbstractFilterControl): string[] | null {
        const errors = this.filters
            .map((f) => f.checkRemove(filter))
            .filter((err) => err !== null);
        
        if (!errors.length) {
            filter.destroy();
            return null;
        }

        return errors;
    }

    /**
     * 
     */
    removeAllFilters() {
        this.filters.forEach((f) => f.destroy(true));
        this.filters = [];

        this.updateLayerViewModel().then(() => {
            this.eventChannel.fireEvent("onFilterRemoved");
        }).fail(es.error.handleJSError);
    }

    /**
     *
     * @param filter
     * @param fromFilter
     * @param toFilter
     */
    moveFilter(filter: AbstractFilterControl, fromFilter: OrFilterControl | AndFilterControl | null, toFilter: OrFilterControl | AndFilterControl | null) {
        if (!fromFilter) {
            this.filters.splice(this.filters.indexOf(filter), 1);
        } else {
            fromFilter.removeFilter(filter, true);
        }

        if (!toFilter) {
            this.filters.push(filter);
        } else {
            toFilter.addFilter(filter);
        }
    }

    /**
     * Set a new attribute info. Will remove deprecated filters
     * 
     * @param newAttributes
     * @param newFilterRanges
     */
    setAttributesInfo(newAttributes: Attribute[], newFilterRanges) {
        const newAttrNames = newAttributes.map(a => a.name);
        const oldAttrNames = this.filterObject.attributes.map(a => a.name);
        const filtersLength = this.filters.length;
        this.filterObject.attributes = newAttributes;
        this.layerInfo.featureInfo.attributesRange = newFilterRanges;
        oldAttrNames
            .filter((attr) => !newAttrNames.includes(attr))
            .forEach((removedAttr) => this.removeAttributeFilter(removedAttr, true)); // silently remove filter, update once if needed

        if (filtersLength !== this.filters.length) {
            this._refresh();
        }
    }

    /**
     * 
     * @param edits 
     * 
     * @returns 
     */
    // using TS private instead of JS private so we can use bind
    private onEditFeatures(edits: any[]) {
        const isLayerViewOutdated = edits.find((e) => {
            return e.action.layerViewId === this.layerViewModel.getId();
        });

        if (isLayerViewOutdated) {
            this.fetchUsedRanges().then(() => {
                var checkboxFiltersAll = this.filters.filter((f) => {
                    return f.type == 'Checkbox' && f.isAll();
                }).map((f) => {
                    return f.attrName;
                });

                this._refresh().done(() => {
                    this.filters
                        .filter((f) => f.type == 'Checkbox' && checkboxFiltersAll.includes(f.attrName) && !f.isAll())
                        .forEach((f) => f.toggleAll());
                });
            });
        }

        if (es.isPublicAccess) {
            return; // we can not do anything
        }

        this.updateInteracts(edits);
    }

    /**
     * 
     * @param numFeatures 
     * @param layerViewId 
     */
    // using TS private instead of JS private so we can use bind
    private onBatchEditFeatures(numFeatures: number, layerViewId: number) {
        if (layerViewId === this.layerViewModel.getId()) {
            this.fetchUsedRanges().then(this._refresh);
        }
    }

    /**
     * 
     * @param layerViewObject 
     */
    // using TS private instead of JS private so we can use bind
    private onUpdateLayerView(layerViewObject: ILayerView) {
        // TOR9-17202 In analysis we have internal attrs used in filters, attrs are not listed but never removed... ignore them
        const specialInternalAttrsUsedInFilters = ['ES_AGGR_ID', 'ES_AGGR_VALUE'];
        if (layerViewObject.id === this.layerViewModel.getId()) {
            this.getUsedAttributes()
                    .filter(a => !specialInternalAttrsUsedInFilters.includes(a) && !layerViewObject.attributeNames.includes(a))
                    .forEach(a => this.removeAttributeFilter(a));

            const layerInfo = this.map.layer.getById(layerViewObject.id).getLayerInfo();
            this.filterObject.attributes = layerInfo.attributes;
            this.filterObject.filterRanges = layerInfo.featureInfo.attributeRanges;

            // If we are using the InScheduleFilter, we need to add the ES_ID to the layer attributes
            const leafFiltersTypes = this.getFilters().flatMap(getLeafFilters).map(f => f.getType());
            if(leafFiltersTypes.includes("InSchedule")) {
                this.filterObject.attributes.push({name: "ES_ID", title: "ES_ID", attrType: "INTEGER", internal: true});
            }
        }
    };

    /**
     * 
     * @returns 
     */
    // using TS private instead of JS private so we can use bind
    private _init() {
        this.clearFilters();

        var attrNames = es.util.criteria.getAttrs(this.layerViewModel.getCriteriaObjRef())?.filter(attr => attr !== "ES_ID");
        es.util.criteria.getAttrs(this.filterObject.FeatureSourceCriteria, attrNames);
        return this.fetchLayerInfo(attrNames).then(() => {
            /*
            if (_this === null) {
                es.log.warn("trying to init already destroyed filter control. Aborting ...");
                return;
            }
            */
            let fsCriteriaDfd = null;
            if (!es.isPublicAccess) { // public users can't see locked filters
                fsCriteriaDfd = this.createFromCriteria(this.filterObject.FeatureSourceCriteria, true, true);
            }
            return Promise.all([fsCriteriaDfd, this.createFromCriteria(this.layerViewModel.getCriteriaClone(), false, true)]);
        }).then(() => {
            if (this.filters.length) {
                this.eventChannel.fireEvent("onFilterAdded");
            }
        });
    }

    /**
     * 
     * @param filter 
     * @param parent 
     * @param silent 
     * 
     * @returns 
     */
    // using TS private instead of JS private so we can use bind
    private onFilterCreated(filter: AbstractFilterControl, parent, silent?: boolean): AbstractFilterControl {
        if (parent){
            parent.addFilter(filter);
        } else {
            this.filters.push(filter);
        }

        filter.getEventChannel()
            .addListener("onUpdate", this.updateLayerViewModel)
            .addListener("onLockChanged", this.onLockChanged, this)
            .addListener("onDestroy", this.onRemovedFilter);
        
        if (!silent) {
            this.eventChannel.fireEvent("onFilterAdded", filter);
        }

        return filter;
    }

    /**
     * 
     * @returns 
     */
    // using TS private instead of JS private so we can use bind
    private updateLayerViewModel() {
        let lvCriteria = this.filters.reduce((array, f) => {
            if (!f.isLocked()) {
                Array.prototype.push.apply(array, f.getCriteriaClone());
            }
            return array;
        }, []);

        lvCriteria = es.util.criteria.unflattenAnd(...lvCriteria);
        lvCriteria = lvCriteria?.$type === "All" ? null : lvCriteria;

        const dasDfd = this.filters.map(function (f) {
            return f.getDynamicAttributes(lvCriteria);
        });

        return es.util.deferred.all(dasDfd).then((dynamicAttributes) => {
            dynamicAttributes = dynamicAttributes.reduce((array, da) => {
                Array.prototype.push.apply(array, da);
                return array;
            }, []); // flatten the result, dynamaicAttributes is an array of array

            const fda = this.layerViewModel.getDynamicAttributes().filter((da) => this.fixedDynamicAttributes.includes(da.name));
            if (fda.length) {
                dynamicAttributes.push.apply(dynamicAttributes, fda);
            }

            let attributeNames = this.layerViewModel.getAttributeNames();

            // Remove attribute names from dynamicAttributes no longer used
            let removedAttributes = this.getRemovedAttributes(dynamicAttributes);
            attributeNames = attributeNames.filter((attrName) => {
                return removedAttributes.findIndex((ra) => ra.name === attrName) === -1;
            });

            // Add to attribute names new dynamicAttributes at the beginning of the list
            attributeNames = this.getAddedAttributes(dynamicAttributes)
                .map((a) => a.name)
                .concat(attributeNames);

            const lvmUpdate = {
                'attributeNames': attributeNames,
                'dynamicAttributes': dynamicAttributes,
                'criteria': lvCriteria
            };

            if (this.needResetLabelTemplate(dynamicAttributes)) {
                const styleSettings = this.layerViewModel.getStyleSettingsClone();
                styleSettings.labelSettings.labelTemplate = "{{" + attributeNames[0] + "}}";
                lvmUpdate.styleSettings = styleSettings;
            }

            this.suppressOnSetCriteria = true;
            return this.layerViewModel.updateLayerView(lvmUpdate)
                .then(() => lvmUpdate)
                .always(() => { this.suppressOnSetCriteria = false; });
        }).then((lvmUpdate) => {
            this.eventChannel.fireEvent("onUpdateModelFromControls", lvmUpdate, this.layerViewModel.getId(), lvCriteria);
        });
    }

    /**
     * 
     * @param filter 
     */
    // using TS private instead of JS private so we can use bind
    private onLockChanged(filter: AbstractFilterControl) {
        if (filter.locked && !this.canLockFilter(filter)) {
            throw new Error("This should not be possible");
        }

        //Move filter to the top if it was nested
        const parent = es.control.filterUtil.parentOf(this.filters, filter);
        if (parent && (filter.isLocked() || parent.isLocked())) {
            if (parent) {
                this.moveFilter(filter, parent, null);
            }
        }


        this.updateFeatureSourceCriteria(filter, this.layerViewModel.getId())
            .then(() => this.fetchUsedRanges())
            .then(this._refresh)
            .fail(es.error.handleJSError);
    };

    /**
     * 
     * @param filter 
     */
    // using TS private instead of JS private so we can use bind
    private onRemovedFilter(filter: AbstractFilterControl) {
        const index = this.filters.indexOf(filter);
        if (index >= 0) {
            this.filters.splice(this.filters.indexOf(filter), 1);
            let dfd = this.updateLayerViewModel();
            if (filter.affectOtherLayer()) {
                dfd = Promise.all([this.removeForOtherLayer(filter), dfd]);
            }
            dfd.then(() => {
                this.eventChannel.fireEvent("onFilterRemoved");
            }).catch(es.error.handleJSError);
        }
    }

    /**
     * 
     * @param filter 
     * 
     * @returns 
     */
    private removeForOtherLayer(filter: AbstractFilterControl) {
        const otherLVM = this.map.layer.getById(filter.getOtherLayerId());
        const updatedCrit = es.control.filterUtil.removeFilterCriteria(otherLVM.getCriteriaClone(), filter.getOtherLayerCriteria());
        return otherLVM.updateLayerView({criteria: updatedCrit});
    }

    /**
     * Re-render every existing filter. Mostly useful when the range are changed.
     * This will not merge criteria of same attributes: should use init() to do so
     */
    // using TS private instead of JS private so we can use bind
    private _refresh() {
        const oldFilters = this.getFilters();
         // locked filter must be first
        oldFilters.sort((a, b) => b.isLocked() - a.isLocked());

        this.filters.length = 0;
        const filtersDfd = oldFilters.map((f) => this.recreateFilter(f));  // filter type might change ... just re-create all of them
        return es.util.deferred.all(filtersDfd).then(() => {
            this.eventChannel.fireEvent("onFilterRefreshed");
        }).fail(es.error.handleJSError);
    }

    /**
     * Silently destroy and recreate a filter.
     * Allow the filter type to change
     * 
     * @param filter
     * 
     * @returns 
     */
    private recreateFilter(filter: AbstractFilterControl): JQueryDeferred {
        var criteria = filter.toCriteria(true);
        filter.destroy(true);
        if (criteria) {
            return this.createFromCriteria(criteria, filter.isLocked(), true);
        }
        return es.util.deferred.resolved();
    }

    /**
     * 
     */
    private clearFilters() {
        this.filters.forEach((f) => f.destroy(true));
        this.filters.length = 0;
    }

    /**
     * 
     * @returns 
     */
    private getUsedAttributes() {
        return this.filters.reduce((used, filter) => {
            if (filter.getType() === "And" || filter.getType() === "Or") {
                Array.prototype.push.apply(used, filter.getAttributesNames());
            }
            else if (filter.getAttrName()) {
                used.push(filter.getAttrName());
            }
            return used;
        }, []).filter(attr => attr !== "ES_ID");
    }

    private getAddedAttributes(newAttributes: Attribute[]): Attribute[] {
        var das = this.layerViewModel.getDynamicAttributes();
        return newAttributes.filter((a) => {
            return das.find((da) => da.name === a.name) === undefined;
        });
    }

    /**
     * 
     * @param newAttributes 
     * 
     * @returns 
     */
    private getRemovedAttributes(newAttributes: Attribute[]): Attribute[] {
        return this.layerViewModel.getDynamicAttributes().filter((a) => {
            return newAttributes.find((da) => da.name === a.name) === undefined && !this.fixedDynamicAttributes.includes(a.name);
        });
    }

    /**
     * 
     * @param newAttributes 
     * 
     * @returns 
     */
    private needResetLabelTemplate(newAttributes: Attribute[]): boolean {
        const removedAttributes = this.getRemovedAttributes(newAttributes);
        const labelAttrs = this.layerViewModel.getStyleSettingsObjRef().labelSettings.labelAttrs;
        return removedAttributes.some((attr) => labelAttrs.includes(attr.name));
    }

    /**
     * 
     * @returns 
     */
    private fetchUsedRanges() {
        return this.fetchLayerInfo(this.getUsedAttributes());
    }

    /**
     * 
     * @param attrNames 
     * 
     * @returns 
     */
    private fetchLayerInfo(attrNames: string[]) {
        return es.api.layerInfo.getForLayerView(this.layerViewModel.getId(), attrNames,{noCache :true}).then((info) => {
            /*
            if (_this === null) {
                es.log.warn("Fetching already destroyed object, aborting ...");
                return;
            }
            */
            this.layerInfo.featureInfo = info.featureInfo;

            this.filters.forEach((f) => f.setRange(info.featureInfo.attributeRanges));
        });
    }

    /**
     * 
     * @param edits 
     * 
     * @returns 
     */
    private updateInteracts(edits: any[]) {
        const spatialFilter = this.getSpatialFilter();
        const isInteracts = spatialFilter !== undefined && spatialFilter.getType() === "Interacts";

        if (!isInteracts || !spatialFilter.getSoftLink() || spatialFilter.isLocked()) {
            return;
        }

        const softLink = spatialFilter.getSoftLink();
        const bufferEdit = edits.find((edit) => {
            return softLink.layerViewId === edit.action.layerViewId && softLink.featureId === edit.action.featureId;
        });

        if (bufferEdit && bufferEdit.$type === "UpdateResult") {
            es.api.geoShape.store({
                geom: bufferEdit.action.geom
            }).then((geoShape) => {
                // TOR9-12150 - Need to get it again as could have been destroyed/changed in the mean time
                const spatialFilter = this.getSpatialFilter();
                const isInteracts = spatialFilter !== undefined && spatialFilter.getType() === "Interacts";

                // is it gone?! or still there?
                if (!isInteracts || !spatialFilter.getSoftLink() || spatialFilter.isLocked()) {
                    return;
                }

                spatialFilter.updateBuffer(bufferEdit.action.attrs, geoShape.key, bufferEdit.label);
            });
        } else if (bufferEdit && bufferEdit.$type === "RemoveResult") {
            spatialFilter.destroy();
        }
    }

    /**
     * 
     * @param filter 
     * @param layerId 
     * @param layerViewIdMapping 
     * 
     * @returns 
     */
    private updateFeatureSourceCriteria(filter: AbstractFilterControl, layerId: number, layerViewIdMapping?: any) {
        this.map.eventChannel.fireEvent("onSlowActionStart");

        let dfd = this.updateLayerViewFSCriteria(layerId, filter.toCriteria(true), filter.isLocked());
        if (filter.getType() === "Aggregate") {
            // if it's an aggregate, lock the other criteria as well
            let otherLayerId = filter.getOtherLayerId();
            if (layerViewIdMapping) {
                otherLayerId = layerViewIdMapping[otherLayerId];
            }

            dfd = $.when(dfd, this.updateLayerViewFSCriteria(otherLayerId, filter.getOtherLayerCriteria(), filter.isLocked()));
        }

        const matchingFilter = this.filters.find((f) => f.match(filter));
        if (matchingFilter) {
            dfd.done(function() {
                matchingFilter.merge(filter);
            });
        }

        return dfd.always(() => {
            this.map.eventChannel.fireEvent("onSlowActionFinish");
        }).done(() => {
            var updatedLayers = [layerId];
            if (filter.getType() === "Aggregate") {
                updatedLayers.push(filter.getOtherLayerId());
            }

            this.map.eventChannel.fireEvent("onUpdateFeatureSourceCriteria", updatedLayers);
        });
    }

    private updateLayerViewFSCriteria(layerId: number, criteria: Criteria, locked: boolean): JQueryDeferred<void> {
        return $.when(
            es.api.layerView.get(layerId),
            es.api.layerView.getFeatureSourceCriterias(layerId)
        ).then((layerView, fsCriteria) => {
            var updatedFsCriteria;
            if (locked) {
                const updatedCriteria = es.control.filterUtil.removeFilterCriteria(layerView.criteria, criteria);
                layerView.criteria = updatedCriteria == null ? null : es.util.criteria.unflattenAnd(
                    ...es.control.filterUtil.groupByAttribute([updatedCriteria])
                );
                updatedFsCriteria = es.util.criteria.unflattenAnd(
                    ...es.control.filterUtil.groupByAttribute([es.util.criteria.unflattenAnd(fsCriteria.removableCriteria, criteria)])
                );
            } else {
                layerView.criteria = es.util.criteria.unflattenAnd(
                    ...es.control.filterUtil.groupByAttribute([es.util.criteria.unflattenAnd(criteria, layerView.criteria)])
                );
                const updatedCriteria = es.control.filterUtil.removeFilterCriteria(fsCriteria.removableCriteria, criteria);
                updatedFsCriteria = updatedCriteria == null ? null : es.util.criteria.unflattenAnd(
                    ...es.control.filterUtil.groupByAttribute([updatedCriteria])
                );
            }
            
            return es.api.layerView.updateWithRemovableCriteria(layerView, updatedFsCriteria).done((lvrs, rcrs) => {
                if (layerId === this.layerViewModel.getId()) {
                    this.filterObject.FeatureSourceCriteria = rcrs.removableCriteria;
                }
                
                return lvrs;
            });
        }).then(function(layerView) {
            es.api.appResources.setWorkingCopyChanged(layerView.geoMapId);
        });
    }

    /**
     * 
     * @param attributeName 
     * @param silent 
     */
    private removeAttributeFilter(attributeName: string, silent?: boolean) {
        let fs = this.filters;
        if (this.filters.length == 1 && this.filters[0].type == 'Or') {
            fs = this.filters[0].getFilters();
        }

        var i = fs.findIndex(f => f.getAttrName() === attributeName);
        if (i !== -1) {
            var filter = fs[i];
            if (silent) {
                // if not silent, the onDestroy will be fired and callback will remove it
                fs.splice(i, 1);
            }
            filter.destroy(silent);
        } else {
            es.log.info("Could not find filter for attributeName: " + attributeName);
        }
    }
}

function getLeafFilters(filter: AbstractFilterControl) {
    if (["Or", "And"].includes(filter.getType())) {
        return filter.getFilters().flatMap(getLeafFilters);
    }

    return filter;
}
